import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, SearchIcon, XIcon, StarIcon, FlameIcon, TrendingUpIcon, TrendingDownIcon, SparklesIcon } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, memo, useRef } from "react";
import { ProductIcon } from "@/components/product-icon";
import { type ProductWithMarketData } from "@/features/product/product-options";
import { useMarketStats } from "@/backend/hooks/use-market-stats";
import { useMarkets } from "@/solana/hooks/use-markets";
import { useMarkets as useDynamicMarkets } from "@/domains/markets";
import { ABSOLUTE_MAX_LEVERAGE } from "@/solana/constants";
import { useFavorites } from "@/features/favorites/use-favorites";
import { useMarketPrice } from "@/backend/store";
import { useMarketPrecision } from "@/hooks/use-price-precision";
import { type AssetClass, type TokenCategory } from "@/domains/markets/types";
import { getCategoriesForAssetClass } from "@nordex/core";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { useProductSelection } from "./use-product-selection";

// ============================================================================
// STABLE PRODUCT LIST - Loaded from markets context (all 75 markets!)
// ============================================================================
type StableProduct = {
  id: string;
  symbol: string;
  baseCode: string;
  quoteCode: string;
  assetClass: AssetClass;
  tokenCategory: TokenCategory;
  createdAt?: string;
};

// Get stable products list - either from API or fallback
const useStableProducts = (): StableProduct[] => {
  const { markets } = useDynamicMarkets();
  
  if (markets && markets.length > 0) {
    return markets.map(m => ({
      id: m.symbol,
      symbol: m.symbol,
      baseCode: m.baseSymbol,
      quoteCode: m.quoteSymbol,
      assetClass: m.assetClass ?? "crypto",
      tokenCategory: m.tokenCategory ?? "other",
      createdAt: m.createdAt,
    }));
  }
  
  // Fallback during loading
  return [];
};

// ============================================================================
// LIVE DATA CELLS - Subscribe directly to market data (like positions table)
// These cells re-render independently when their market's data updates
// ============================================================================

// Price cell - subscribes to live price for this specific market
const MarketPriceCell = memo(function MarketPriceCell({ 
  marketId 
}: { 
  marketId: string 
}) {
  const { markets } = useMarkets();
  const marketData = markets?.find(m => m.symbol === marketId);
  
  // Use real-time price from Zustand store (WebSocket updates)
  const lastPrice = useMarketPrice(marketId);
  const priceChangePercent24h = marketData?.priceChangePercent24h;
  
  // Get market precision dynamically from backend config
  const { quotePrecision } = useMarketPrecision(marketId);
  
  return (
    <div className="text-right">
      <div className="text-xs font-medium text-foreground">
        {formatPriceWithPrecision(lastPrice, quotePrecision)}
      </div>
      <div
        className={cn(
          "text-[10px]",
          getPriceChangeColor(priceChangePercent24h)
        )}
      >
        {formatPercentage(priceChangePercent24h)}
      </div>
    </div>
  );
});

// Desktop price change cell
const DesktopPriceChangeCell = memo(function DesktopPriceChangeCell({ 
  marketId 
}: { 
  marketId: string 
}) {
  const { markets } = useMarkets();
  const marketData = markets?.find(m => m.symbol === marketId);
  
  const priceChange24h = marketData?.priceChange24h;
  const priceChangePercent24h = marketData?.priceChangePercent24h;
  
  return (
    <div
      className={cn(
        "text-right text-xs",
        getPriceChangeColor(priceChangePercent24h)
      )}
    >
      {formatPriceChange(priceChange24h, priceChangePercent24h)}
    </div>
  );
});

// Funding rate cell
const FundingRateCell = memo(function FundingRateCell({ 
  marketId 
}: { 
  marketId: string 
}) {
  const { markets } = useMarkets();
  const marketData = markets?.find(m => m.symbol === marketId);
  const { stats: marketStats } = useMarketStats();
  const stats = marketStats.find(s => s.market === marketId);
  
  // Use WebSocket data (stats) if available, fallback to localStorage (marketData)
  const fundingRate = stats?.funding_rate 
    ? parseFloat(stats.funding_rate) 
    : marketData?.fundingRate;
  
  return (
    <div
      className={cn(
        "text-right text-xs",
        getFundingRateColor(fundingRate)
      )}
    >
      {formatFundingRate(fundingRate)}
    </div>
  );
});

// Volume cell
const VolumeCell = memo(function VolumeCell({ 
  marketId 
}: { 
  marketId: string 
}) {
  const { markets } = useMarkets();
  const marketData = markets?.find(m => m.symbol === marketId);
  const { stats: marketStats } = useMarketStats();
  const stats = marketStats.find(s => s.market === marketId);
  
  // Try stats first (WebSocket), fallback to marketData
  const volume24h = stats?.volume_24h_usd 
    ? parseFloat(stats.volume_24h_usd) 
    : marketData?.volume24h;
  
  return (
    <div className="text-right text-xs text-muted-foreground">
      {formatVolume(volume24h)}
    </div>
  );
});

// Open interest cell
const OpenInterestCell = memo(function OpenInterestCell({ 
  marketId 
}: { 
  marketId: string 
}) {
  const { markets } = useMarkets();
  const marketData = markets?.find(m => m.symbol === marketId);
  const { stats: marketStats } = useMarketStats();
  const stats = marketStats.find(s => s.market === marketId);
  
  // Use WebSocket data (stats) if available, fallback to localStorage (marketData)
  // Note: marketData.openInterest is in base currency, needs conversion to USD
  const openInterestUsd = stats?.open_interest_usd 
    ? parseFloat(stats.open_interest_usd) 
    : marketData?.openInterest && marketData?.currentPrice 
      ? marketData.openInterest * marketData.currentPrice 
      : undefined;
  
  return (
    <div className="text-right text-xs text-muted-foreground">
      {formatOpenInterest(openInterestUsd)}
    </div>
  );
});

// ============================================================================
// MARKET ROW COMPONENTS - Stable symbol/structure, dynamic data from cells
// ============================================================================

// Mobile market row
const MobileMarketRow = memo(function MobileMarketRow({
  product,
  isSelected,
  onSelect,
}: {
  product: StableProduct;
  isSelected: boolean;
  onSelect: (productId: string) => void;
}) {
  const { stats: marketStats } = useMarketStats();
  const stats = marketStats.find(s => s.market === product.id);
  const maxLeverage = stats?.max_leverage ?? ABSOLUTE_MAX_LEVERAGE;
  const { isFavorite, toggleFavorite } = useFavorites();
  const isFav = isFavorite(product.id);
  
  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection
    toggleFavorite(product.id);
  };
  
  return (
    <div
      onClick={() => onSelect(product.id)}
      className={cn(
        "px-3 py-3 cursor-pointer hover:bg-accent active:bg-accent/80",
        isSelected && "bg-accent/50"
      )}
    >
      <div className="grid grid-cols-12 gap-2 items-center">
        {/* Symbol + Leverage Badge (stacked) - STABLE */}
        <div className="col-span-5 flex items-center gap-2">
          <button
            onClick={handleStarClick}
            className="flex-shrink-0 hover:scale-110 transition-transform"
            aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
          >
            <StarIcon 
              className={cn(
                "size-3.5 transition-colors",
                isFav 
                  ? "fill-yellow-500 text-yellow-500" 
                  : "text-muted-foreground/50 hover:text-muted-foreground"
              )}
            />
          </button>
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-foreground">
                {product.baseCode}
              </span>
              <span className="text-xs text-muted-foreground">-{product.quoteCode}</span>
            </div>
            <LeverageBadge leverage={maxLeverage} />
          </div>
        </div>
        {/* Volume - DYNAMIC */}
        <div className="col-span-3">
          <VolumeCell marketId={product.id} />
        </div>
        {/* Price + Change - DYNAMIC */}
        <div className="col-span-4">
          <MarketPriceCell marketId={product.id} />
        </div>
      </div>
    </div>
  );
});

// Desktop market row
const DesktopMarketRow = memo(function DesktopMarketRow({
  product,
  isSelected,
  onSelect,
}: {
  product: StableProduct;
  isSelected: boolean;
  onSelect: (productId: string) => void;
}) {
  const { stats: marketStats } = useMarketStats();
  const stats = marketStats.find(s => s.market === product.id);
  const maxLeverage = stats?.max_leverage ?? ABSOLUTE_MAX_LEVERAGE;
  const { isFavorite, toggleFavorite } = useFavorites();
  const isFav = isFavorite(product.id);
  
  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row selection
    toggleFavorite(product.id);
  };
  
  return (
    <div
      onClick={() => onSelect(product.id)}
      className={cn(
        "px-3 py-2.5 cursor-pointer hover:bg-accent active:bg-accent/80",
        isSelected && "bg-accent/50"
      )}
    >
      <div className="flex items-center">
        {/* Symbol + Leverage Badge - STABLE */}
        <div className="min-w-[140px] flex-[1.4] flex items-center gap-2">
          <button
            onClick={handleStarClick}
            className="flex-shrink-0 hover:scale-110 transition-transform"
            aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
          >
            <StarIcon 
              className={cn(
                "size-3.5 transition-colors",
                isFav 
                  ? "fill-yellow-500 text-yellow-500" 
                  : "text-muted-foreground/50 hover:text-muted-foreground"
              )}
            />
          </button>
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-medium text-foreground whitespace-nowrap">
              {product.baseCode}-{product.quoteCode}
            </span>
            <LeverageBadge leverage={maxLeverage} />
          </div>
        </div>
        {/* Market Price - DYNAMIC */}
        <div className="min-w-[80px] flex-1">
          <MarketPriceCell marketId={product.id} />
        </div>
        {/* 24H Change - DYNAMIC */}
        <div className="min-w-[100px] flex-[1.2]">
          <DesktopPriceChangeCell marketId={product.id} />
        </div>
        {/* 8H Funding - DYNAMIC */}
        <div className="min-w-[70px] flex-[0.8]">
          <FundingRateCell marketId={product.id} />
        </div>
        {/* Volume - DYNAMIC */}
        <div className="min-w-[80px] flex-1">
          <VolumeCell marketId={product.id} />
        </div>
        {/* Open Interest - DYNAMIC */}
        <div className="min-w-[80px] flex-1">
          <OpenInterestCell marketId={product.id} />
        </div>
      </div>
    </div>
  );
});

// Leverage badge component
const LeverageBadge = ({ leverage }: { leverage?: number }) => {
  if (!leverage) return null;
  return (
    <span className="inline-flex items-center w-fit px-1.5 py-0.5 text-[10px] font-medium rounded bg-emerald-500/20 text-emerald-400">
      {leverage}x
    </span>
  );
};

// Tab button component for market filtering
const TabButton = ({ 
  active, 
  onClick, 
  children, 
  badge, 
  disabled 
}: { 
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  badge?: number;
  disabled?: boolean;
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0",
        "hover:bg-accent/50",
        active && "bg-accent text-foreground",
        !active && "text-muted-foreground",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      {children}
      {badge !== undefined && badge > 0 && (
        <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-primary/20 text-primary">
          {badge}
        </span>
      )}
    </button>
  );
};

// Formatting utilities
// Format price with dynamic precision based on market config
const formatPriceWithPrecision = (price?: number, precision: number = 2) => {
  if (!price || price === 0) return "-";
  return price.toLocaleString("en-US", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

const formatPercentage = (percent?: number) => {
  if (percent === undefined || percent === null) return "-";
  const sign = percent >= 0 ? "+" : "";
  return `${sign}${percent.toFixed(2)}%`;
};

const formatPriceChange = (absolute?: number, percent?: number) => {
  if (absolute === undefined || percent === undefined) return formatPercentage(percent);
  const sign = absolute >= 0 ? "+" : "";
  const absFormatted = Math.abs(absolute) >= 1000 
    ? `${sign}${(absolute / 1000).toFixed(1)}K`
    : `${sign}${absolute.toFixed(2)}`;
  return `${absFormatted} / ${formatPercentage(percent)}`;
};

const formatVolume = (volume?: number) => {
  if (!volume || volume === 0) return "-";
  if (volume >= 1e9) {
    return `$${(volume / 1e9).toFixed(2)}B`;
  }
  if (volume >= 1e6) {
    return `$${(volume / 1e6).toFixed(2)}M`;
  }
  if (volume >= 1e3) {
    return `$${(volume / 1e3).toFixed(2)}K`;
  }
  return `$${volume.toFixed(2)}`;
};

const formatOpenInterest = (oi?: number) => {
  if (!oi || oi === 0) return "-";
  if (oi >= 1e9) {
    return `$${(oi / 1e9).toFixed(2)}B`;
  }
  if (oi >= 1e6) {
    return `$${(oi / 1e6).toFixed(2)}M`;
  }
  if (oi >= 1e3) {
    return `$${(oi / 1e3).toFixed(2)}K`;
  }
  return `$${oi.toFixed(2)}`;
};

const formatFundingRate = (rate?: number) => {
  if (rate === undefined || rate === null) return "-";
  const percent = rate * 100;
  const sign = percent >= 0 ? "+" : "";
  return `${sign}${percent.toFixed(4)}%`;
};

const getPriceChangeColor = (percent?: number) => {
  if (percent === undefined || percent === null || percent === 0) return "text-muted-foreground";
  return percent > 0 ? "text-buy" : "text-sell";
};

const getFundingRateColor = (rate?: number) => {
  if (rate === undefined || rate === null || rate === 0) return "text-muted-foreground";
  return rate >= 0 ? "text-buy" : "text-sell";
};

// Standard ProductSelect component (for desktop/non-mobile use)
export const ProductSelect = () => {
  const { products, selectedProduct, onChange, isLoading } = useProductSelection();

  return (
    <div className="flex flex-col gap-2">
      <MarketSelector
        products={products}
        selectedProduct={selectedProduct}
        onChange={onChange}
        isLoading={isLoading}
      />
    </div>
  );
};

// Mobile Market Sheet - can be controlled externally
type MobileMarketSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

// Quick filter type for mobile sheet
type MobileQuickFilter = "all" | "favorites" | "hot" | "gainers" | "losers" | "new";
type MobileAssetFilter = "all" | "crypto" | "stocks" | "forex";
type MobileCategoryFilter = "all" | string;

// Mobile asset filters
const MOBILE_ASSET_FILTERS: { id: MobileAssetFilter; label: string; disabled?: boolean }[] = [
  { id: "all", label: "All" },
  { id: "crypto", label: "Crypto" },
  { id: "stocks", label: "Stocks", disabled: true },
  { id: "forex", label: "Forex", disabled: true },
];

export const MobileMarketSheet = ({ open, onOpenChange }: MobileMarketSheetProps) => {
  const { products, selectedProduct, onChange } = useProductSelection();
  const stableProducts = useStableProducts();
  const [searchQuery, setSearchQuery] = useState("");
  const [quickFilter, setQuickFilter] = useState<MobileQuickFilter>("all");
  const [assetFilter, setAssetFilter] = useState<MobileAssetFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<MobileCategoryFilter>("all");
  const { favorites } = useFavorites();
  const { markets: marketsData } = useMarkets();

  // Get available categories based on selected asset class
  const availableCategories = useMemo(() => {
    if (assetFilter === "all") {
      return getCategoriesForAssetClass("crypto");
    }
    return getCategoriesForAssetClass(assetFilter as AssetClass);
  }, [assetFilter]);

  // Reset category filter when asset class changes
  useEffect(() => {
    setCategoryFilter("all");
  }, [assetFilter]);

  // Filter STABLE products based on search (no live data, never re-renders on price updates)
  const filteredProductIds = useMemo(() => {
    if (!searchQuery.trim()) {
      return stableProducts.map((p: StableProduct) => p.id);
    }

    const query = searchQuery.toLowerCase();
    return stableProducts
      .filter((p: StableProduct) =>
        p.symbol.toLowerCase().includes(query) ||
        p.baseCode.toLowerCase().includes(query)
      )
      .map((p: StableProduct) => p.id);
  }, [searchQuery, stableProducts]);

  const filteredProducts = useMemo(() => {
    return stableProducts.filter((p: StableProduct) => filteredProductIds.includes(p.id));
  }, [filteredProductIds, stableProducts]);

  // Apply all filters
  const finalFilteredProducts = useMemo(() => {
    let result = [...filteredProducts];

    // 1. Apply asset class filter
    if (assetFilter !== "all") {
      result = result.filter(p => p.assetClass === assetFilter);
    }

    // 2. Apply category filter
    if (categoryFilter !== "all") {
      result = result.filter(p => p.tokenCategory === categoryFilter);
    }

    // 3. Apply quick filter (behavior-based)
    switch (quickFilter) {
      case "favorites":
        result = result.filter(p => favorites.includes(p.id));
        break;
      case "hot": {
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aVolume = aData?.volume24h ?? 0;
          const bVolume = bData?.volume24h ?? 0;
          return bVolume - aVolume;
        });
        break;
      }
      case "gainers": {
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aChange = aData?.priceChangePercent24h ?? 0;
          const bChange = bData?.priceChangePercent24h ?? 0;
          return bChange - aChange;
        });
        break;
      }
      case "losers": {
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aChange = aData?.priceChangePercent24h ?? 0;
          const bChange = bData?.priceChangePercent24h ?? 0;
          return aChange - bChange;
        });
        break;
      }
      case "new": {
        result = result.sort((a, b) => {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bDate - aDate;
        });
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        result = result.filter(p => {
          if (!p.createdAt) return false;
          return new Date(p.createdAt).getTime() > thirtyDaysAgo;
        });
        break;
      }
    }

    return result;
  }, [filteredProducts, quickFilter, assetFilter, categoryFilter, favorites, marketsData]);

  const handleProductSelect = useCallback((productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      onChange(product);
    }
    onOpenChange(false);
    setSearchQuery("");
  }, [products, onChange, onOpenChange]);

  const handleSwipeClose = useCallback(() => {
    onOpenChange(false);
    setSearchQuery("");
  }, [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] flex flex-col"
        onSwipeClose={handleSwipeClose}
      >
        <SheetHeader className="flex-shrink-0">
          <SheetTitle>Select Market</SheetTitle>
          <SheetClose asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <XIcon className="size-4" />
            </Button>
          </SheetClose>
        </SheetHeader>

        <div className="flex flex-col flex-1 min-h-0">
          {/* Search Bar */}
          <div className="p-3 border-b">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search markets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
          </div>

          {/* Mobile: Filter Dropdowns Row */}
          <div className="px-3 py-2 border-b flex items-center gap-2">
            {/* Quick Filter Dropdown */}
            <select
              value={quickFilter}
              onChange={(e) => setQuickFilter(e.target.value as MobileQuickFilter)}
              className="bg-background border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All</option>
              <option value="favorites">★ Fav{favorites.length > 0 ? ` (${favorites.length})` : ''}</option>
              <option value="hot">🔥 Hot</option>
              <option value="gainers">📈 Up</option>
              <option value="losers">📉 Down</option>
              <option value="new">✨ New</option>
            </select>

            {/* Asset Class Filter Dropdown */}
            <select
              value={assetFilter}
              onChange={(e) => setAssetFilter(e.target.value as MobileAssetFilter)}
              className="bg-background border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {MOBILE_ASSET_FILTERS.map((filter) => (
                <option key={filter.id} value={filter.id} disabled={filter.disabled}>
                  {filter.label}{filter.disabled ? ' (Soon)' : ''}
                </option>
              ))}
            </select>

            {/* Category Filter Dropdown */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as MobileCategoryFilter)}
              className="bg-background border border-border rounded-md px-2 py-1.5 text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="all">All Types</option>
              {availableCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.label}</option>
              ))}
            </select>
          </div>

          {/* Table Header - Mobile: 3 columns */}
          <div className="px-3 py-2 border-b bg-muted/50">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground">
              <div className="col-span-5">Symbol</div>
              <div className="col-span-3 text-right">Volume</div>
              <div className="col-span-4 text-right">Market Price</div>
            </div>
          </div>

          {/* Table Body - Mobile Layout */}
          <ScrollArea className="flex-1">
            {finalFilteredProducts.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-sm text-muted-foreground px-4 text-center">
                {quickFilter === "favorites" && favorites.length === 0 
                  ? "No favorites yet. Tap the star icon to add markets."
                  : quickFilter === "new"
                  ? "No new markets in the last 30 days."
                  : "No markets found"}
              </div>
            ) : (
              <div>
                {finalFilteredProducts.map((product) => (
                  <MobileMarketRow
                    key={product.id}
                    product={product}
                    isSelected={selectedProduct?.id === product.id}
                    onSelect={handleProductSelect}
                  />
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// MARKET LIST CONTENT COMPONENTS - Extracted outside to prevent recreation
// ============================================================================

// Quick filters (behavior-based)
type QuickFilter = "all" | "favorites" | "hot" | "gainers" | "losers" | "new";

// Asset class filter
type AssetFilter = "all" | "crypto" | "stocks" | "forex";

// Category filter (context-aware based on asset class)
type CategoryFilter = "all" | TokenCategory;

type MarketListContentProps = {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filteredProducts: StableProduct[];
  selectedProductId?: string;
  onSelect: (productId: string) => void;
};

// Quick filter configuration
const QUICK_FILTERS: { id: QuickFilter; label: string; icon?: React.ReactNode }[] = [
  { id: "all", label: "All" },
  { id: "favorites", label: "Favorites" },
  { id: "hot", label: "Hot", icon: <FlameIcon className="w-3 h-3" /> },
  { id: "gainers", label: "Gainers", icon: <TrendingUpIcon className="w-3 h-3" /> },
  { id: "losers", label: "Losers", icon: <TrendingDownIcon className="w-3 h-3" /> },
  { id: "new", label: "New", icon: <SparklesIcon className="w-3 h-3" /> },
];

// Asset class configuration
const ASSET_FILTERS: { id: AssetFilter; label: string; disabled?: boolean }[] = [
  { id: "all", label: "All" },
  { id: "crypto", label: "Crypto" },
  { id: "stocks", label: "Stocks", disabled: true },
  { id: "forex", label: "Forex", disabled: true },
];

// CSS for hiding scrollbar while keeping scroll functionality
const scrollbarHideStyle = {
  msOverflowStyle: 'none' as const,  // IE and Edge
  scrollbarWidth: 'none' as const,   // Firefox
} as const;

// Desktop market list - memoized to prevent re-renders
const DesktopMarketListContent = memo(function DesktopMarketListContent({
  searchQuery,
  onSearchChange,
  filteredProducts,
  selectedProductId,
  onSelect,
}: MarketListContentProps) {
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [assetFilter, setAssetFilter] = useState<AssetFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");
  const { favorites } = useFavorites();
  const { markets: marketsData } = useMarkets();

  // Scroll state for category arrows
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const categoryContentRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  // Start with true since we know categories likely overflow
  const [canScrollRight, setCanScrollRight] = useState(true);

  // Check scroll position to show/hide arrows
  const checkCategoryScroll = useCallback(() => {
    const scrollEl = categoryScrollRef.current;
    if (!scrollEl) return;
    
    const scrollLeft = scrollEl.scrollLeft;
    const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
    
    setCanScrollLeft(scrollLeft > 5);
    // Show right arrow if not at end of scroll
    setCanScrollRight(scrollLeft < maxScroll - 5);
  }, []);

  // Scroll category list by amount
  const scrollCategories = useCallback((direction: 'left' | 'right') => {
    const el = categoryScrollRef.current;
    if (!el) return;
    const amount = direction === 'left' ? -150 : 150;
    el.scrollBy({ left: amount, behavior: 'smooth' });
  }, []);

  // Check scroll on mount
  useEffect(() => {
    const scrollEl = categoryScrollRef.current;
    if (!scrollEl) return;
    
    // Check immediately
    checkCategoryScroll();
    // Also check after a brief delay to ensure DOM is fully rendered
    const timer = setTimeout(checkCategoryScroll, 100);
    
    // Use ResizeObserver to detect when container size changes
    const resizeObserver = new ResizeObserver(() => {
      checkCategoryScroll();
    });
    resizeObserver.observe(scrollEl);
    
    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [checkCategoryScroll]);

  // Get available categories based on selected asset class
  const availableCategories = useMemo(() => {
    if (assetFilter === "all") {
      // Show crypto categories by default (since that's what we have)
      return getCategoriesForAssetClass("crypto");
    }
    return getCategoriesForAssetClass(assetFilter as AssetClass);
  }, [assetFilter]);

  // Force show right arrow when we have many categories (fallback for scroll detection)
  useEffect(() => {
    const categoryCount = availableCategories.length + 1; // +1 for "All"
    if (categoryCount > 5) {
      // Delay to let the scroll check run first, but override if still false
      const timer = setTimeout(() => {
        const scrollEl = categoryScrollRef.current;
        if (scrollEl && scrollEl.scrollLeft === 0) {
          setCanScrollRight(true);
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [availableCategories.length]);

  // Reset category filter when asset class changes
  useEffect(() => {
    setCategoryFilter("all");
    // Reset scroll position when categories change
    if (categoryScrollRef.current) {
      categoryScrollRef.current.scrollLeft = 0;
    }
    setTimeout(checkCategoryScroll, 100);
  }, [assetFilter, checkCategoryScroll]);

  const finalFilteredProducts = useMemo(() => {
    let result = [...filteredProducts];

    // 1. Apply asset class filter
    if (assetFilter !== "all") {
      result = result.filter(p => p.assetClass === assetFilter);
    }

    // 2. Apply category filter
    if (categoryFilter !== "all") {
      result = result.filter(p => p.tokenCategory === categoryFilter);
    }

    // 3. Apply quick filter (behavior-based)
    switch (quickFilter) {
      case "favorites":
        result = result.filter(p => favorites.includes(p.id));
        break;
      case "hot": {
        // Sort by 24h volume (most traded)
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aVolume = aData?.volume24h ?? 0;
          const bVolume = bData?.volume24h ?? 0;
          return bVolume - aVolume;
        });
        break;
      }
      case "gainers": {
        // Sort by positive price change (biggest gainers first)
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aChange = aData?.priceChangePercent24h ?? 0;
          const bChange = bData?.priceChangePercent24h ?? 0;
          return bChange - aChange;
        });
        break;
      }
      case "losers": {
        // Sort by negative price change (biggest losers first)
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aChange = aData?.priceChangePercent24h ?? 0;
          const bChange = bData?.priceChangePercent24h ?? 0;
          return aChange - bChange;
        });
        break;
      }
      case "new": {
        // Sort by creation date (newest first)
        result = result.sort((a, b) => {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bDate - aDate;
        });
        // Only show markets added in the last 30 days
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        result = result.filter(p => {
          if (!p.createdAt) return false;
          return new Date(p.createdAt).getTime() > thirtyDaysAgo;
        });
        break;
      }
    }

    return result;
  }, [filteredProducts, quickFilter, assetFilter, categoryFilter, favorites, marketsData]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Search Bar */}
      <div className="p-3 border-b">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search markets..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Quick Filters Row */}
      <div className="px-3 py-1.5 border-b">
        <div className="flex items-center gap-1">
          {QUICK_FILTERS.map((filter) => (
            <TabButton
              key={filter.id}
              active={quickFilter === filter.id}
              onClick={() => setQuickFilter(filter.id)}
              badge={filter.id === "favorites" && favorites.length > 0 ? favorites.length : undefined}
            >
              <span className="flex items-center gap-1 whitespace-nowrap">
                {filter.icon}
                {filter.label}
              </span>
            </TabButton>
          ))}
        </div>
      </div>

      {/* Asset Class + Category Filters Row with Arrow Navigation */}
      <div className="px-3 py-1.5 border-b bg-muted/30 overflow-hidden">
        <div className="flex items-center gap-2 max-w-full overflow-hidden">
          {/* Asset Class Filter */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {ASSET_FILTERS.map((filter) => (
              <TabButton
                key={filter.id}
                active={assetFilter === filter.id}
                onClick={() => setAssetFilter(filter.id)}
                disabled={filter.disabled}
              >
                <span className="whitespace-nowrap">
                  {filter.label}
                  {filter.disabled && <span className="text-[9px] ml-1 opacity-60">(Soon)</span>}
                </span>
              </TabButton>
            ))}
          </div>

          {/* Divider */}
          <div className="h-4 w-px bg-border flex-shrink-0" />

          {/* Category Filter with Arrow Navigation */}
          <div className="relative flex-1 min-w-0 flex items-center">
            {/* Left Arrow */}
            {canScrollLeft && (
              <button
                onClick={() => scrollCategories('left')}
                className="absolute left-0 top-0 bottom-0 z-10 px-1 flex items-center bg-gradient-to-r from-muted/80 to-transparent hover:from-muted transition-colors"
                aria-label="Scroll categories left"
              >
                <ChevronLeftIcon className="w-4 h-4 text-foreground" />
              </button>
            )}

            {/* Scrollable Category Pills */}
            <div 
              ref={categoryScrollRef}
              onScroll={checkCategoryScroll}
              className={cn(
                "flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden",
                canScrollLeft && "pl-6",
                canScrollRight && "pr-6"
              )}
              style={{ ...scrollbarHideStyle, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <div ref={categoryContentRef} className="flex items-center gap-1 w-max">
                <TabButton
                  active={categoryFilter === "all"}
                  onClick={() => setCategoryFilter("all")}
                >
                  <span className="whitespace-nowrap">All</span>
                </TabButton>
                {availableCategories.map((cat) => (
                  <TabButton
                    key={cat.id}
                    active={categoryFilter === cat.id}
                    onClick={() => setCategoryFilter(cat.id)}
                  >
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <span 
                        className="w-2 h-2 rounded-full flex-shrink-0" 
                        style={{ backgroundColor: cat.color }}
                      />
                      {cat.label}
                    </span>
                  </TabButton>
                ))}
              </div>
            </div>

            {/* Right Arrow */}
            {canScrollRight && (
              <button
                onClick={() => scrollCategories('right')}
                className="absolute right-0 top-0 bottom-0 z-10 px-1 flex items-center bg-gradient-to-l from-muted/80 to-transparent hover:from-muted transition-colors"
                aria-label="Scroll categories right"
              >
                <ChevronRightIcon className="w-4 h-4 text-foreground" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Header - Desktop: 6 columns */}
      <div className="px-3 py-2 border-b bg-muted/50">
        <div className="flex items-center text-xs font-medium text-muted-foreground">
          <div className="min-w-[140px] flex-[1.4]">Symbol</div>
          <div className="min-w-[80px] flex-1 text-right">Market Price</div>
          <div className="min-w-[100px] flex-[1.2] text-right">24H Change</div>
          <div className="min-w-[70px] flex-[0.8] text-right">8H Funding</div>
          <div className="min-w-[80px] flex-1 text-right">Volume</div>
          <div className="min-w-[80px] flex-1 text-right">Open Interest</div>
        </div>
      </div>

      {/* Table Body */}
      <ScrollArea className="h-[400px]">
        {finalFilteredProducts.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground px-4 text-center">
            {quickFilter === "favorites" && favorites.length === 0 
              ? "No favorites yet. Click the star icon to add markets to your favorites."
              : quickFilter === "new"
              ? "No new markets added in the last 30 days."
              : quickFilter === "gainers"
              ? "No gainers to display with current filters."
              : quickFilter === "losers"
              ? "No losers to display with current filters."
              : "No markets found"}
          </div>
        ) : (
          <div>
            {finalFilteredProducts.map((product) => (
              <DesktopMarketRow
                key={product.id}
                product={product}
                isSelected={selectedProductId === product.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
});

// Mobile market list - memoized to prevent re-renders
const MobileMarketListContent = memo(function MobileMarketListContent({
  searchQuery,
  onSearchChange,
  filteredProducts,
  selectedProductId,
  onSelect,
}: MarketListContentProps) {
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [assetFilter, setAssetFilter] = useState<AssetFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>("all");
  const { favorites } = useFavorites();
  const { markets: marketsData } = useMarkets();

  // Get available categories based on selected asset class
  const availableCategories = useMemo(() => {
    if (assetFilter === "all") {
      return getCategoriesForAssetClass("crypto");
    }
    return getCategoriesForAssetClass(assetFilter as AssetClass);
  }, [assetFilter]);

  // Reset category filter when asset class changes
  useEffect(() => {
    setCategoryFilter("all");
  }, [assetFilter]);

  const finalFilteredProducts = useMemo(() => {
    let result = [...filteredProducts];

    // 1. Apply asset class filter
    if (assetFilter !== "all") {
      result = result.filter(p => p.assetClass === assetFilter);
    }

    // 2. Apply category filter
    if (categoryFilter !== "all") {
      result = result.filter(p => p.tokenCategory === categoryFilter);
    }

    // 3. Apply quick filter (behavior-based)
    switch (quickFilter) {
      case "favorites":
        result = result.filter(p => favorites.includes(p.id));
        break;
      case "hot": {
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aVolume = aData?.volume24h ?? 0;
          const bVolume = bData?.volume24h ?? 0;
          return bVolume - aVolume;
        });
        break;
      }
      case "gainers": {
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aChange = aData?.priceChangePercent24h ?? 0;
          const bChange = bData?.priceChangePercent24h ?? 0;
          return bChange - aChange;
        });
        break;
      }
      case "losers": {
        result = result.sort((a, b) => {
          const aData = marketsData?.find(m => m.symbol === a.id);
          const bData = marketsData?.find(m => m.symbol === b.id);
          const aChange = aData?.priceChangePercent24h ?? 0;
          const bChange = bData?.priceChangePercent24h ?? 0;
          return aChange - bChange;
        });
        break;
      }
      case "new": {
        result = result.sort((a, b) => {
          const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return bDate - aDate;
        });
        const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
        result = result.filter(p => {
          if (!p.createdAt) return false;
          return new Date(p.createdAt).getTime() > thirtyDaysAgo;
        });
        break;
      }
    }

    return result;
  }, [filteredProducts, quickFilter, assetFilter, categoryFilter, favorites, marketsData]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Search Bar */}
      <div className="p-3 border-b">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search markets..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>
      </div>

      {/* Mobile: Filter Dropdowns Row */}
      <div className="px-3 py-2 border-b flex items-center gap-2">
        {/* Quick Filter Dropdown */}
        <select
          value={quickFilter}
          onChange={(e) => setQuickFilter(e.target.value as QuickFilter)}
          className="bg-background border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All</option>
          <option value="favorites">★ Fav{favorites.length > 0 ? ` (${favorites.length})` : ''}</option>
          <option value="hot">🔥 Hot</option>
          <option value="gainers">📈 Up</option>
          <option value="losers">📉 Down</option>
          <option value="new">✨ New</option>
        </select>

        {/* Asset Class Filter Dropdown */}
        <select
          value={assetFilter}
          onChange={(e) => setAssetFilter(e.target.value as AssetFilter)}
          className="bg-background border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {ASSET_FILTERS.map((filter) => (
            <option key={filter.id} value={filter.id} disabled={filter.disabled}>
              {filter.label}{filter.disabled ? ' (Soon)' : ''}
            </option>
          ))}
        </select>

        {/* Category Filter Dropdown */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
          className="bg-background border border-border rounded-md px-2 py-1.5 text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Types</option>
          {availableCategories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </select>
      </div>

      {/* Table Header - Mobile: 3 columns */}
      <div className="px-3 py-2 border-b bg-muted/50">
        <div className="grid grid-cols-12 gap-2 text-xs font-medium text-muted-foreground">
          <div className="col-span-5">Symbol</div>
          <div className="col-span-3 text-right">Volume</div>
          <div className="col-span-4 text-right">Market Price</div>
        </div>
      </div>

      {/* Table Body */}
      <ScrollArea className="flex-1">
        {finalFilteredProducts.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground px-4 text-center">
            {quickFilter === "favorites" && favorites.length === 0 
              ? "No favorites yet. Click the star icon to add markets to your favorites."
              : quickFilter === "new"
              ? "No new markets added in the last 30 days."
              : "No markets found"}
          </div>
        ) : (
          <div>
            {finalFilteredProducts.map((product) => (
              <MobileMarketRow
                key={product.id}
                product={product}
                isSelected={selectedProductId === product.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
});

// ============================================================================
// MARKET SELECTOR - Main component
// ============================================================================

type MarketSelectorProps = {
  products: ProductWithMarketData[];
  selectedProduct?: ProductWithMarketData | null;
  onChange: (product: ProductWithMarketData) => void;
  isLoading: boolean;
};

const MarketSelector = ({
  products,
  selectedProduct,
  onChange,
  isLoading,
}: MarketSelectorProps) => {
  const stableProducts = useStableProducts();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const label = useMemo(() => {
    if (isLoading) {
      return "";
    }

    if (!selectedProduct) {
      return "Select product";
    }

    return selectedProduct.symbol;
  }, [isLoading, selectedProduct]);

  // Filter STABLE products based on search (no live data, never re-renders on price updates)
  const filteredProductIds = useMemo(() => {
    if (!searchQuery.trim()) {
      return stableProducts.map(p => p.id);
    }

    const query = searchQuery.toLowerCase();
    return stableProducts
      .filter(p =>
        p.symbol.toLowerCase().includes(query) ||
        p.baseCode.toLowerCase().includes(query)
      )
      .map(p => p.id);
  }, [searchQuery, stableProducts]); // Depends on search query and stable products

  const filteredProducts = useMemo(() => {
    return stableProducts.filter(p => filteredProductIds.includes(p.id));
  }, [filteredProductIds, stableProducts]);

  const handleProductSelect = useCallback((productId: string) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      onChange(product);
    }
    setOpen(false);
    setSearchQuery("");
  }, [products, onChange]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleSwipeClose = useCallback(() => {
    setOpen(false);
    setSearchQuery("");
  }, []);

  // Shared trigger button (for desktop)
  const TriggerButton = (
    <button
      type="button"
      role="combobox"
      aria-expanded={open}
      className="flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer py-1"
    >
      {selectedProduct && (
        <ProductIcon baseCode={selectedProduct.base.code} className="size-6" />
      )}
      <span className="text-xl text-foreground">
        {label}
      </span>
      <ChevronDownIcon className="size-6 text-muted-foreground" />
    </button>
  );

  // Mobile: Use Sheet (full-screen bottom slide)
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <div onClick={() => setOpen(true)}>
          {TriggerButton}
        </div>
        <SheetContent 
          side="bottom" 
          className="h-[90vh] flex flex-col"
          onSwipeClose={handleSwipeClose}
        >
          <SheetHeader className="flex-shrink-0">
            <SheetTitle>Select Market</SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <XIcon className="size-4" />
              </Button>
            </SheetClose>
          </SheetHeader>
          <MobileMarketListContent
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            filteredProducts={filteredProducts}
            selectedProductId={selectedProduct?.id}
            onSelect={handleProductSelect}
          />
        </SheetContent>
      </Sheet>
    );
  }

  // Desktop: Use Popover with wider width
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {TriggerButton}
      </PopoverTrigger>
      <PopoverContent className="w-[800px] p-0" align="start" alignOffset={-12} sideOffset={4}>
        <DesktopMarketListContent
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          filteredProducts={filteredProducts}
          selectedProductId={selectedProduct?.id}
          onSelect={handleProductSelect}
        />
      </PopoverContent>
    </Popover>
  );
};

